import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, CheckCircle2, AlertCircle, ArrowLeft, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();

  const token = searchParams.get("token") || "";
  const emailParam = searchParams.get("email") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("El enlace de restablecimiento es inválido o no contiene un token.");
      return;
    }
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(data.message || "¡Contraseña actualizada exitosamente!");
      if (data.user) {
        setUser(data.user);
      }
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#050505] text-white">
      <div className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
        {/* Decorative Top Gradient */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#E11D48] via-purple-600 to-[#E11D48]" />

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#E11D48]/15 border border-[#E11D48]/30 flex items-center justify-center text-[#E11D48]">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-heading text-white">Restablecer Contraseña</h1>
            <p className="text-xs text-zinc-400">La Caja LGBT Cine & Series</p>
          </div>
        </div>

        {emailParam && (
          <div className="mb-6 p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-zinc-300">
            Restableciendo contraseña para: <strong className="text-white font-medium">{emailParam}</strong>
          </div>
        )}

        {!token ? (
          <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm space-y-3">
            <div className="flex items-center gap-2 font-semibold">
              <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
              <span>Token de restablecimiento no detectado</span>
            </div>
            <p className="text-xs leading-relaxed text-zinc-300">
              Asegúrate de haber abierto el enlace completo recibido en tu correo electrónico o contacta al administrador.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 text-xs text-[#E11D48] hover:underline font-medium pt-2"
            >
              <ArrowLeft className="w-4 h-4" /> Volver a Iniciar Sesión
            </Link>
          </div>
        ) : success ? (
          <div className="text-center py-6 space-y-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto animate-bounce" />
            <h2 className="text-lg font-bold text-white">¡Contraseña Cambiada con Éxito!</h2>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Tu sesión ha sido iniciada automáticamente. Te estamos redirigiendo al catálogo...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 font-medium mb-1.5">
                Nueva Contraseña
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Mínimo 6 caracteres"
                  className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E11D48] transition-colors"
                />
                <Lock className="w-4 h-4 text-zinc-500 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-400 font-medium mb-1.5">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Repite tu nueva contraseña"
                  className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E11D48] transition-colors"
                />
                <Lock className="w-4 h-4 text-zinc-500 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white py-3 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#E11D48]/20 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Actualizando...</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Guardar Nueva Contraseña</span>
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <Link to="/auth" className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Volver al Inicio de Sesión
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
